import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, XCircle, Loader2, Clock, Edit2, Package } from 'lucide-react';
import { productCreationRequestService } from '../../../../services/erpApi';
import { useToast } from '../../../../hooks/useToast';
import { usePermissions } from '../../../../hooks/usePermissions';
import { MODULES, ACTIONS } from '../../../../utils/permissions';
import { Toast } from '../../../../components';
import './ProductCreationRequestDetailPage.css';

interface CreationRequest {
  id: number;
  request_number: string;
  status: 'pending' | 'approved' | 'rejected' | 'cancelled';
  payload?: any;
  created_by?: number;
  created_by_name?: string;
  approved_by?: number;
  approved_by_name?: string;
  approved_at?: string;
  rejection_notes?: string;
  product_id?: number;
  created_at: string;
}

const parsePayload = (payload: any): Record<string, any> => {
  if (!payload) return {};
  if (typeof payload === 'string') {
    try {
      return JSON.parse(payload);
    } catch {
      return {};
    }
  }
  return payload;
};

const formatCurrency = (value: any) => {
  if (value == null || value === '') return '—';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return '—';
  return num.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const ProductCreationRequestDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const { hasPermission } = usePermissions();
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [request, setRequest] = useState<CreationRequest | null>(null);
  const [createdProductId, setCreatedProductId] = useState<number | null>(null);

  const canApprove = hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCT_REQUEST_APPROVE);
  const canReject = hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCT_REQUEST_REJECT);
  const canCreate = hasPermission(MODULES.PRODUCTS, ACTIONS.PRODUCT_REQUEST_CREATE);
  const canEditRequest = canCreate || canApprove;

  const loadRequest = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const response = await productCreationRequestService.getById(id);
      if (response.success && response.data) {
        setRequest(response.data);
        if (response.data.product_id) {
          setCreatedProductId(response.data.product_id);
        }
      } else {
        showToast('Solicitação não encontrada', 'error');
        navigate('/erp/produtos/solicitacoes');
      }
    } catch (error: any) {
      showToast('Erro ao carregar solicitação', 'error');
      navigate('/erp/produtos/solicitacoes');
    } finally {
      setLoading(false);
    }
  }, [id, showToast, navigate]);

  useEffect(() => {
    if (id) {
      loadRequest();
    }
  }, [id, loadRequest]);

  const handleApprove = async () => {
    if (!id || !request) return;

    if (!window.confirm('Tem certeza que deseja aprovar esta solicitação? O produto será criado.')) {
      return;
    }

    setProcessing(true);
    try {
      const response = await productCreationRequestService.approve(id);
      if (response.success) {
        const productId =
          response.data?.product_id ||
          response.data?.product?.id ||
          null;
        if (productId) {
          setCreatedProductId(productId);
        }
        showToast(response.message || 'Solicitação aprovada e produto criado com sucesso', 'success');
        loadRequest();
      } else {
        showToast(response.message || 'Erro ao aprovar solicitação', 'error');
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || 'Erro ao aprovar solicitação', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!id || !request) return;

    const notes = window.prompt('Informe o motivo da rejeição:');
    if (notes === null) return;

    setProcessing(true);
    try {
      const response = await productCreationRequestService.reject(id, notes);
      if (response.success) {
        showToast('Solicitação rejeitada', 'success');
        loadRequest();
      } else {
        showToast(response.message || 'Erro ao rejeitar solicitação', 'error');
      }
    } catch (error: any) {
      showToast(error?.response?.data?.message || 'Erro ao rejeitar solicitação', 'error');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'pending': return { label: 'Pendente', color: 'status-pending', icon: Clock };
      case 'approved': return { label: 'Aprovada', color: 'status-approved', icon: CheckCircle2 };
      case 'rejected': return { label: 'Rejeitada', color: 'status-rejected', icon: XCircle };
      case 'cancelled': return { label: 'Cancelada', color: 'status-cancelled', icon: XCircle };
      default: return { label: 'Desconhecido', color: 'status-default', icon: Clock };
    }
  };

  if (loading) {
    return (
      <div className="product-creation-detail-loading">
        <Loader2 size={32} className="spinner" />
        <p>Carregando...</p>
      </div>
    );
  }

  if (!request) {
    return null;
  }

  const statusConfig = getStatusConfig(request.status);
  const StatusIcon = statusConfig.icon;
  const payload = parsePayload(request.payload);
  const productId = createdProductId || request.product_id;

  return (
    <div className="product-creation-detail">
      <div className="product-creation-detail-header">
        <button
          className="product-creation-detail-back"
          onClick={() => navigate('/erp/produtos/solicitacoes')}
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1>{request.request_number}</h1>
          <div className="product-creation-detail-status">
            <StatusIcon size={16} />
            <span className={statusConfig.color}>{statusConfig.label}</span>
          </div>
        </div>
      </div>

      <div className="product-creation-detail-content">
        <div className="product-creation-detail-card">
          <h3>Informações da Solicitação</h3>
          <div className="product-creation-detail-info">
            <div>
              <label>Criado por</label>
              <p>{request.created_by_name || `Usuário #${request.created_by}`}</p>
            </div>
            <div>
              <label>Data de Criação</label>
              <p>{new Date(request.created_at).toLocaleString('pt-BR')}</p>
            </div>
            {request.approved_by_name && (
              <>
                <div>
                  <label>{request.status === 'rejected' ? 'Rejeitado por' : 'Aprovado por'}</label>
                  <p>{request.approved_by_name}</p>
                </div>
                <div>
                  <label>Data</label>
                  <p>{request.approved_at ? new Date(request.approved_at).toLocaleString('pt-BR') : '—'}</p>
                </div>
              </>
            )}
            {request.rejection_notes && (
              <div className="product-creation-detail-full">
                <label>Motivo da Rejeição</label>
                <p>{request.rejection_notes}</p>
              </div>
            )}
            {request.status === 'approved' && productId && (
              <div className="product-creation-detail-full">
                <label>Produto criado</label>
                <p>
                  <Link
                    to={`/erp/produtos/produtos/${productId}`}
                    className="product-creation-detail-product-link"
                  >
                    <Package size={14} />
                    Abrir produto #{productId}
                  </Link>
                </p>
              </div>
            )}
          </div>
        </div>

        <div className="product-creation-detail-card">
          <h3>Dados do Produto Proposto</h3>
          <div className="product-creation-detail-info">
            <div>
              <label>SKU</label>
              <p>{payload.sku || '—'}</p>
            </div>
            <div>
              <label>Código de Barras</label>
              <p>{payload.barcode || '—'}</p>
            </div>
            <div className="product-creation-detail-full">
              <label>Nome</label>
              <p>{payload.name || '—'}</p>
            </div>
            {payload.description && (
              <div className="product-creation-detail-full">
                <label>Descrição</label>
                <p>{payload.description}</p>
              </div>
            )}
            <div>
              <label>Preço de Custo</label>
              <p>{formatCurrency(payload.costPrice)}</p>
            </div>
            <div>
              <label>Preço de Venda</label>
              <p>{formatCurrency(payload.price)}</p>
            </div>
            <div>
              <label>Grupo</label>
              <p>{payload.groupId != null ? `#${payload.groupId}` : '—'}</p>
            </div>
            <div>
              <label>Marca</label>
              <p>{payload.brandId != null ? `#${payload.brandId}` : '—'}</p>
            </div>
            <div>
              <label>Unidade</label>
              <p>{payload.unitId != null ? `#${payload.unitId}` : '—'}</p>
            </div>
            <div>
              <label>Status do produto</label>
              <p>{payload.active === false ? 'Inativo' : 'Ativo'}</p>
            </div>
            <div>
              <label>Estoque Mínimo</label>
              <p>{payload.minStock != null ? payload.minStock : '—'}</p>
            </div>
            <div>
              <label>Estoque Máximo</label>
              <p>{payload.maxStock != null ? payload.maxStock : '—'}</p>
            </div>
          </div>
        </div>

        {request.status === 'pending' && (canEditRequest || canApprove || canReject) && (
          <div className="product-creation-detail-actions">
            {canEditRequest && (
              <button
                type="button"
                onClick={() => navigate(`/erp/produtos/solicitacoes/${id}/editar`)}
                className="product-creation-detail-edit"
              >
                <Edit2 size={18} />
                Editar
              </button>
            )}
            {canReject && (
              <button
                onClick={handleReject}
                disabled={processing}
                className="product-creation-detail-reject"
              >
                <XCircle size={18} />
                Rejeitar
              </button>
            )}
            {canApprove && (
              <button
                onClick={handleApprove}
                disabled={processing}
                className="product-creation-detail-approve"
              >
                {processing ? <Loader2 size={18} className="spinner" /> : <CheckCircle2 size={18} />}
                {processing ? 'Processando...' : 'Aprovar e Criar Produto'}
              </button>
            )}
          </div>
        )}
      </div>

      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default ProductCreationRequestDetailPage;
